(function () {
    const CONFIG_STORAGE_KEY = "algolia_config_duplicated";
    const RETRY_LIMIT = 40;
    const RETRY_DELAY = 300;
    const WATCH_WINDOW_MS = RETRY_LIMIT * RETRY_DELAY;
    const CONSENT_GRANTED = "yes";

    const normalize = (value) =>
        value !== undefined && value !== null
            ? value.toString().trim().toLowerCase()
            : "";

    const normalizeId = (value) => {
        const str = normalize(value);
        const digits = str.replace(/\D/g, "");
        return digits || str;
    };

    const normalizeMarketToken = (value) => normalize(value).replace(/[-\s]+/g, "_");

    const normalizePortugalSuffix = (suffix) =>
        suffix.startsWith("portugal_")
            ? suffix
                .replace(/_(en)(?:_pt)?$/, "_$1")
                .replace(/_(pt)(?:_pt)?$/, "_$1")
            : suffix;

    const normalizeIndexSuffix = (suffix) => {
        const normalizedSuffix = normalizePortugalSuffix(normalizeMarketToken(suffix)
            .replace(/[^a-z0-9_]+/g, "_")
            .replace(/_+/g, "_")
            .replace(/^_+|_+$/g, ""));

        return normalizedSuffix ? `_${normalizedSuffix}` : "";
    };

    const normalizeIndexName = (indexName) => {
        if (!indexName) return indexName;

        return indexName
            .toString()
            .replace(/(_portugal)_(en)(?:[-_]pt)?(?=(_query_suggestions)?$)/i, "$1_$2")
            .replace(/(_portugal)_(pt)(?:[-_]pt)?(?=(_query_suggestions)?$)/i, "$1_$2");
    };

    const getLocaleCandidates = (shopifyData) => {
        const locale = normalizeMarketToken(shopifyData?.market?.language?.shop_locale?.locale);
        const shopifyLocale = normalizeMarketToken(window.Shopify?.locale);
        const country = normalizeMarketToken(shopifyData?.country || window.Shopify?.country);
        const candidates = locale ? [locale] : [shopifyLocale];

        candidates.forEach((candidate) => {
            if (candidate && country && !candidate.endsWith(`_${country}`)) {
                candidates.push(`${candidate}_${country}`);
            }
        });

        return candidates.filter(Boolean);
    };

    const marketMatchesLocale = (market, localeCandidates = []) => {
        const marketKey = normalizeMarketToken(market?.key);
        const marketSuffix = normalizeMarketToken(market?.suffix);
        const marketIndex = normalizeMarketToken(market?.index_name);

        return localeCandidates.some((locale) =>
            marketKey.includes(locale) ||
            marketSuffix.includes(locale) ||
            marketIndex.includes(locale),
        );
    };

    const getVisitorAnalyticsConsent = () => {
        try {
            return normalize(
                window.Shopify?.customerPrivacy?.currentVisitorConsent?.()?.analytics,
            );
        } catch (error) {
            console.debug("Algolia index sync: failed to read visitor consent", error);
            return "";
        }
    };

    const hasExplicitAnalyticsConsent = () =>
        getVisitorAnalyticsConsent() === CONSENT_GRANTED;

    const clearStoredConfig = () => {
        localStorage.removeItem(CONFIG_STORAGE_KEY);
    };

    const resolveIndexName = (config, shopifyData) => {
        if (!config) return null;

        const indexPrefix = config.index_prefix
            ? config.index_prefix
            : config.index_name && config.index_name.includes("products")
                ? config.index_name.split("products")[0]
                : "";

        const marketId = normalizeId(shopifyData?.market?.id);
        const localeCandidates = getLocaleCandidates(shopifyData);
        const country = normalize(shopifyData?.country || window.Shopify?.country);

        let currentMarket = null;
        if (Array.isArray(config.markets_mapping)) {
            currentMarket =
                config.markets_mapping.find(
                    (market) => normalizeId(market.id) === marketId && marketMatchesLocale(market, localeCandidates),
                ) ||
                config.markets_mapping.find((market) => normalizeId(market.id) === marketId) ||
                config.markets_mapping.find(
                    (market) =>
                        marketMatchesLocale(market, localeCandidates) ||
                        (country && normalize(market.key).includes(country)),
                ) ||
                config.markets_mapping[0];
        }

        const resolvedSuffix = normalizeIndexSuffix(currentMarket?.suffix);
        if (!indexPrefix) {
            return normalizeIndexName(currentMarket?.index_name || config.index_name || null);
        }

        if (!currentMarket) {
            return normalizeIndexName(config.index_name || null);
        }

        return normalizeIndexName(`${indexPrefix}products${resolvedSuffix}`);
    };

    const updateLocalStorage = () => {
        try {
            const config = window.algoliaShopify?.config;
            const shopifyData = window.algoliaShopify?.shopify;
            const analyticsConsent = getVisitorAnalyticsConsent();
            console.debug("Algolia index sync: config snapshot", {
                hasConfig: !!config,
                hasShopifyData: !!shopifyData,
                analyticsConsent,
                marketId: shopifyData?.market?.id,
                indexName: config?.index_name,
                indexPrefix: config?.index_prefix,
                marketsMapping: config?.markets_mapping?.length,
            });

            if (!config || !shopifyData?.market?.id) {
                console.debug("Algolia index sync: missing config or market id", {
                    hasConfig: !!config,
                    marketId: shopifyData?.market?.id,
                    marketsMapping: config?.markets_mapping?.length,
                });
                return { success: false, reason: "missing-config-or-market" };
            }

            const resolvedIndex = resolveIndexName(config, shopifyData);
            if (!resolvedIndex) {
                console.debug("Algolia index sync: unable to resolve index", {
                    configIndex: config.index_name,
                    indexPrefix: config.index_prefix,
                    marketId: shopifyData?.market?.id,
                    locale: shopifyData?.market?.language?.shop_locale?.locale,
                    country: shopifyData?.country || window.Shopify?.country,
                    marketsMapping: config?.markets_mapping?.length,
                });
                return { success: false, reason: "cannot-resolve-index" };
            }

            const resolvedQuerySuggestionsIndex = normalizeIndexName(config?.query_suggestions_index_name);

            config.index_name = resolvedIndex;
            if (resolvedQuerySuggestionsIndex) {
                config.query_suggestions_index_name = resolvedQuerySuggestionsIndex;
            }

            if (!hasExplicitAnalyticsConsent()) {
                clearStoredConfig();
                console.debug("Algolia index sync: analytics consent not granted", {
                    analyticsConsent,
                    resolvedIndex,
                });
                return { success: false, reason: "analytics-consent-not-granted" };
            }

            const storedConfigRaw = localStorage.getItem(CONFIG_STORAGE_KEY);
            let storedConfig = {};
            try {
                storedConfig = storedConfigRaw ? JSON.parse(storedConfigRaw) : {};
            } catch (parseError) {
                console.debug("Algolia index sync: failed to parse stored config", parseError);
            }

            const updatedConfig = {
                ...storedConfig,
                app_id: storedConfig.app_id || config?.app_id,
                search_api_key: storedConfig.search_api_key || config?.search_api_key,
                index_prefix: storedConfig.index_prefix || config?.index_prefix,
                query_suggestions_index_name: normalizeIndexName(
                    storedConfig.query_suggestions_index_name ||
                    config?.query_suggestions_index_name,
                ),
                markets_mapping: config?.markets_mapping || storedConfig.markets_mapping,
                user_token: storedConfig.user_token || config?.user_token,
                index_name: resolvedIndex,
            };

            localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(updatedConfig));

            console.debug("Algolia index sync: updated config", {
                marketId: shopifyData?.market?.id,
                locale: shopifyData?.market?.language?.shop_locale?.locale,
                country: shopifyData?.country || window.Shopify?.country,
                resolvedIndex,
                marketsMapping: config?.markets_mapping?.length,
                storedConfig,
                updatedConfig,
            });
            return { success: true, updatedConfig };
        } catch (error) {
            console.error("Algolia config index sync failed", error);
            return { success: false, reason: "exception", error };
        }
    };

    const initUpdate = () => {
        console.debug("Algolia index sync: init");
        const initialResult = updateLocalStorage();
        console.log("Algolia index sync: initialResult", initialResult);
        if (initialResult?.success) {
            console.log("Algolia index sync: updated local storage", initialResult.updatedConfig);
            document.removeEventListener("algolia.hooks.initialize", initUpdate);
            return;
        }

        let attempts = 0;
        const intervalId = setInterval(() => {
            attempts += 1;
            const result = updateLocalStorage();
            if (result?.success || attempts >= RETRY_LIMIT) {
                console.log("Algolia index sync: stopping retries", {
                    attempts,
                    success: result?.success,
                    lastUpdate: result?.updatedConfig,
                    lastReason: result?.reason,
                });
                clearInterval(intervalId);
                document.removeEventListener("algolia.hooks.initialize", initUpdate);
            }
        }, RETRY_DELAY);

        setTimeout(() => {
            if (attempts < RETRY_LIMIT) return;
            console.debug("Algolia index sync: reached watch window without update", {
                attempts,
                marketsMapping: window.algoliaShopify?.config?.markets_mapping?.length,
            });
        }, WATCH_WINDOW_MS + 50);
    };

    document.addEventListener("algolia.hooks.initialize", initUpdate);
    if (document.readyState === "complete" || document.readyState === "interactive") {
        initUpdate();
    } else {
        document.addEventListener("DOMContentLoaded", initUpdate);
    }
})();
